import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./appraisalWizard.css";

const SelfAppraisalEvaluate = () => {
  const { requestId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* ================= FETCH ================= */
  useEffect(() => {
    if (!requestId) {
      toast.error("Invalid appraisal request");
      history.push("/SelfAppraisalList");
      return;
    }

    axios
      .get(`${API_BASE_URL}/self-appraisal/${requestId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        if (res.data.expired) {
          toast.error("Appraisal expired");
          history.push("/SelfAppraisalList");
          return;
        }

        if (res.data.is_answerd === 1) {
          history.push("/SelfAppraisalList");
          return;
        }

        setQuestions(res.data.questions || []);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load appraisal");
        history.push("/SelfAppraisalList");
      });
  }, [requestId, history]);

  /* ================= SUBMIT ================= */
  const submit = () => {
    if (Object.keys(answers).length !== questions.length) {
      toast.error("Please answer all questions");
      return;
    }

    if (comment.trim() === "") {
      toast.error("Comment field cannot be empty.");
      return;
    }

    setSubmitting(true);

    axios
      .post(
        `${API_BASE_URL}/self-appraisal/submit`,
        {
          request_id: requestId,
          comment: comment.trim(),
          answers: Object.values(answers),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      .then(() => {
        toast.success("Self appraisal submitted");
        setTimeout(() => history.push("/SelfAppraisalList"), 1200);
      })
      .catch(() => {
        toast.error("Submission failed");
        setSubmitting(false);
      });
  };

  /* ================= UI ================= */
  if (loading) {
    return <div className="card p-4 text-center">Loading...</div>;
  }

  const total = questions.length;
  const isConfirmStep = step === total;
  const current = questions[step];

  /* Progress mirrors the CI wizard: one step per question plus the confirm step */
  const progress = Math.round((100 / (total + 1)) * (step + 1));

  return (
    <div className="card">
      <ToastContainer position="bottom-right" />

      <div className="card-header with-elements">
        <span className="card-header-title mr-2">Self Appraisal form</span>
      </div>

      <div className="card-body">
        <div className="row">
          <div className="col-md-12">
            <div className="container-fluid">
              <div className="row justify-content-center">
                <div className="col-lg-8 col-md-10 text-center p-0 mb-2">
                  <div className="wizardbox px-0 pt-4 pb-0 mt-3 mb-3">
                    <div className="appraisal-wizard">
                      <div className="progress">
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          aria-valuemin="0"
                          aria-valuemax="100"
                          aria-valuenow={progress}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <br />

                      {!isConfirmStep ? (
                        <fieldset className="quest">
                          <div className="form-card">
                            <div className="row">
                              <div className="col-12">
                                <h5 className="steps">
                                  Questions {step + 1} - {total}
                                </h5>
                              </div>
                              <div className="col-12">
                                <h2 className="fs-title">
                                  {current.question_title}
                                </h2>
                              </div>
                            </div>

                            <div className="row">
                              {current.options.map((opt) => (
                                <React.Fragment key={opt.value_id}>
                                  <div className="col-2">
                                    <input
                                      type="radio"
                                      className="selfradio"
                                      name={`q_${current.question_id}`}
                                      checked={
                                        answers[current.question_id]
                                          ?.option_id === opt.value_id
                                      }
                                      onChange={() =>
                                        setAnswers({
                                          ...answers,
                                          [current.question_id]: {
                                            question_id: current.question_id,
                                            option_id: opt.value_id,
                                            score: opt.score,
                                            comment: "",
                                          },
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-10 mb-2">
                                    <label className="fieldlabels">
                                      {opt.value_title}
                                    </label>
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>

                          <input
                            type="button"
                            className="action-button"
                            value="Next"
                            onClick={() => {
                              if (!answers[current.question_id]) {
                                toast.error(
                                  "Please select a correct answer for this question."
                                );
                                return;
                              }
                              setStep(step + 1);
                            }}
                          />
                          {step > 0 && (
                            <input
                              type="button"
                              className="action-button-previous"
                              value="Previous"
                              onClick={() => setStep(step - 1)}
                            />
                          )}
                        </fieldset>
                      ) : (
                        <fieldset>
                          <div className="form-card">
                            <div className="row">
                              <div className="col-12">
                                <input
                                  className="form-control"
                                  type="text"
                                  name="commt"
                                  value={comment}
                                  onChange={(e) => setComment(e.target.value)}
                                />
                              </div>
                              <div className="col-12">
                                <h5 className="steps">Confirm</h5>
                              </div>
                              <div className="col-12">
                                <h2 className="fs-title">Are you sure?</h2>
                              </div>
                            </div>

                            <div className="row align-items-center">
                              <div className="col-6">
                                <h2>Confirm to send.!</h2>
                              </div>
                              <div className="col-6">
                                <input
                                  type="button"
                                  className="action-button"
                                  value={submitting ? "Sending..." : "Submit"}
                                  disabled={submitting}
                                  onClick={submit}
                                />
                              </div>
                            </div>
                          </div>

                          <input
                            type="button"
                            className="action-button"
                            value="Cancel"
                            disabled={submitting}
                            onClick={() => history.push("/SelfAppraisalList")}
                          />
                          <input
                            type="button"
                            className="action-button-previous"
                            value="Previous"
                            disabled={submitting}
                            onClick={() => setStep(step - 1)}
                          />
                        </fieldset>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfAppraisalEvaluate;
